import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { AlenDevice } from "src/interfaces/IAlenDevice";
import { Floor } from "src/interfaces/IZone";
import {
  ALEN_PRODUCT_TYPE_MAP,
  groupDevicesByProductType,
  WRITABLE_PRODUCT_TYPES,
} from "src/services/alen/utils";
import { getCommonActions } from "src/services/alen/utils/actions";
import {
  ScheduleDto,
  SchedulableAction,
  ScheduleTargetLevel,
  ScheduleTargetSelection,
} from "./types";

const AlenScheduleContext = createContext<AlenSchedulingContextType | null>(
  null
);
const useAlenSchedulingContext = () => {
  const context = useContext(AlenScheduleContext);
  if (!context) {
    throw new Error(
      "useAlenSchedulingContext must be used within a AlenSchedulingProvider"
    );
  }
  return context;
};

type AlenSchedulingContextType = {
  alenPropertyId: number;
  propertyName: string;
  floors: Floor[];
  alenDevices: AlenDevice[];
  newScheduling: ScheduleDto;
  setScheduleTarget: (target: ScheduleTargetSelection) => void;
  commonActions: {
    standardName: keyof SchedulableAction;
    options: SchedulableAction[keyof SchedulableAction][];
  }[];
  setNewScheduling: React.Dispatch<React.SetStateAction<ScheduleDto>>;
  isScheduleValid: boolean;
};

export const getDefaultNewScheduling = (propertyId: number): ScheduleDto => ({
  locationId: propertyId,
  schedulerName: "",
  schedulerType: "BULK",
  deviceGroups: [],
  frequency: { repeat: "CUSTOM", days: [] },
});

export const AlenSchedulingProvider = ({
  children,
  alenPropertyId,
  propertyName,
  floors,
  devices,
}: {
  children: React.ReactNode;
  alenPropertyId: number;
  propertyName: string;
  floors: Floor[];
  devices: AlenDevice[];
}) => {
  const [newScheduling, setNewScheduling] = useState<ScheduleDto>(
    getDefaultNewScheduling(alenPropertyId)
  );

  const [commonActions, setCommonActions] = useState<
    {
      standardName: keyof SchedulableAction;
      options: SchedulableAction[keyof SchedulableAction][];
    }[]
  >([]);

  const setScheduleTarget = useCallback(
    (target: ScheduleTargetSelection) => {
      let targetDevices;
      if (target.level === ScheduleTargetLevel.PROPERTY) {
        targetDevices = devices.filter((device) =>
          WRITABLE_PRODUCT_TYPES.includes(
            ALEN_PRODUCT_TYPE_MAP[device.product_id]
          )
        );
      } else if (target.level === ScheduleTargetLevel.FLOOR) {
        throw new Error("Not implemented");
      } else if (target.level === ScheduleTargetLevel.DEVICE_TYPE) {
        targetDevices = devices.filter((device) =>
          target.value.includes(ALEN_PRODUCT_TYPE_MAP[device.product_id])
        );
      } else {
        targetDevices = devices.filter((device) =>
          target.value.includes(device.id)
        );
      }
      const deviceGroups = groupDevicesByProductType(targetDevices);
      setNewScheduling((prev) => ({
        ...prev,
        deviceGroupType: target.level,
        deviceGroups,
        timezone: `GMT${devices[0].time_zone}`,
      }));
      setCommonActions(
        getCommonActions(targetDevices, true) as unknown as {
          standardName: keyof SchedulableAction;
          options: SchedulableAction[keyof SchedulableAction][];
        }[]
      );
    },
    [devices]
  );

  const isScheduleValid = useMemo(
    () =>
      newScheduling.deviceGroups.length > 0 &&
      newScheduling.deviceGroups.every(
        (group) => group.deviceDetails.length > 0
      ) &&
      newScheduling.actionCode !== undefined &&
      newScheduling.schedulerCode !== undefined &&
      newScheduling.actionStartTime !== undefined &&
      newScheduling.actionStartValue !== undefined &&
      newScheduling.schedulerName !== "" &&
      newScheduling.frequency.days.length > 0 &&
      newScheduling.deviceGroupType !== undefined,
    [newScheduling]
  );

  const contextValue = useMemo(
    () => ({
      alenPropertyId,
      propertyName,
      floors,
      alenDevices: devices,
      newScheduling,
      commonActions,
      setScheduleTarget,
      setNewScheduling,
      isScheduleValid,
    }),
    [
      alenPropertyId,
      propertyName,
      floors,
      devices,
      newScheduling,
      commonActions,
      setScheduleTarget,
      setNewScheduling,
      isScheduleValid,
    ]
  );
  return (
    <AlenScheduleContext.Provider value={contextValue}>
      {children}
    </AlenScheduleContext.Provider>
  );
};

export { AlenScheduleContext, useAlenSchedulingContext };
